import React from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from '@material-ui/styles'
import { Badge } from '@material-ui/core'
import { FilterPopover } from '../Atoms'
import { DateFilter, MultipleValueFilter, RadioListFilter, TreeFilter } from '../Molecules'
import { useParams } from '../../utils/Hooks'
import { UrlManipulation } from '../../utils'
import { dutch, french } from '../../i18n/languages'


const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'nowrap',
    },
    filterDropdown: {
        marginRight: 10,
    }
}))

// Value is the value used in the Elasticsearch-query in the backend
const filterConfig = {
    deed_type: {
        tree: [
            {
                value: "real_estate",
                children: [
                    { value: "sales_of_hand" },
                    { value: "statutes_of_co_ownership"},
                ]
            }, {
                value: "family",
                children: [
                    { value: "donation" },
                    { value: "divorce_by_mutual_consent" },
                ]
            }, {
                value: "enterprise",
                children: [
                    { value: "establishment_of_company" },
                ]
            },
        ]
    },
    deed_land_register_divisions_region: {
        values: [
            { value: "BE-VLG" },
            { value: "BE-BRU" },
            { value: "BE-WAL" },
        ],
    },
    region: {
        values: [
            { value: "BE-VLG" },
            { value: "BE-BRU" },
            { value: "BE-WAL" },
        ],
    },
    clause_lang: {
        values: [
            { value: dutch },
            { value: french },
        ],
    },
    year: {
        values: [
            new Date('2000-01-01'),
            new Date()
        ],
    },
    subsectionsForClauses: {
        values: [
            { value: null, label: "all" },
            { value: "bookmarks", label: "bookmarks" },
            { value: "quality", label: "quality" },
            { value: "own_clauses", label: "own_clauses" },
            { value: "models", label: "models" },
        ],
    },
    subsectionsForModels: {
        values: [
            { value: null, label: "all" },
            { value: "models", label: "models" },
        ],
    },
}

const FilterBar = () => {
    const classes = useStyles()
    const { t } = useTranslation()
    const params = useParams()

    const updateFilter = key => value => {
        UrlManipulation.goToSearch({
            [key]: value,
            page: 1,
        })
    }

    const yearIsCustom = params.year && params.year.length > 0 && (params.year[0] !== filterConfig.year.values[0] || params.year[1] !== filterConfig.year.values[1])
    const isModelsPage = params.subsection === 'models'
    const isClausesPage = !isModelsPage

    return (
        <div className={classes.container}>
            {isClausesPage &&
                <Badge badgeContent={params.deed_type.length} color="primary" className={classes.filterDropdown}>
                    <FilterPopover
                        title={t('filters.deed_type')}
                    >
                        <TreeFilter
                            treeOptions={filterConfig.deed_type.tree}
                            values={params.deed_type}
                            setValues={updateFilter('deed_type')}
                        />
                    </FilterPopover>
                </Badge>
            }

            {isClausesPage &&
                <Badge badgeContent={yearIsCustom ? ' ' : 0} color="primary" className={classes.filterDropdown}>
                    <FilterPopover
                        title={t('filters.year')}
                    >
                        <DateFilter
                            range={filterConfig.year.values}
                            values={params.year}
                            setValues={updateFilter('year')}
                        />
                    </FilterPopover>
                </Badge>
            }

            {isClausesPage &&
                <Badge badgeContent={params.deed_land_register_divisions_region.length} color="primary" className={classes.filterDropdown}>
                    <FilterPopover
                        title={t('filters.deed_land_register_divisions_region')}
                    >
                        <MultipleValueFilter
                            type="checkbox"
                            options={filterConfig.deed_land_register_divisions_region.values}
                            values={params.deed_land_register_divisions_region}
                            setValues={updateFilter('deed_land_register_divisions_region')}
                        />
                    </FilterPopover>
                </Badge>
            }

            {isClausesPage &&
                <Badge badgeContent={params.clause_lang.length} color="primary" className={classes.filterDropdown}>
                    <FilterPopover
                        title={t('filters.language')}
                    >
                        <MultipleValueFilter
                            type="checkbox"
                            options={filterConfig.clause_lang.values}
                            values={params.clause_lang}
                            setValues={updateFilter('clause_lang')}
                        />
                    </FilterPopover>
                </Badge>
            }

            {isClausesPage &&
                <Badge badgeContent={params.subsection ? ' ' : 0} color="primary" className={classes.filterDropdown}>
                    <FilterPopover
                        title={t('filters.subsection')}
                    >
                        <RadioListFilter
                            options={filterConfig.subsectionsForClauses.values}
                            activeValue={params.subsection}
                            setValues={updateFilter('subsection')}
                        />
                    </FilterPopover>
                </Badge>
            }

            {isModelsPage &&
                <Badge badgeContent={params.region.length} color="primary" className={classes.filterDropdown}>
                    <FilterPopover
                        title={t('filters.region')}
                    >
                        <MultipleValueFilter
                            type="checkbox"
                            options={filterConfig.region.values}
                            values={params.region}
                            setValues={updateFilter('region')}
                        />
                    </FilterPopover>
                </Badge>
            }

            {isModelsPage &&
                <Badge badgeContent={params.subsection ? ' ' : 0} color="primary" className={classes.filterDropdown}>
                    <FilterPopover
                        title={t('filters.subsection')}
                    >
                        <RadioListFilter
                            options={filterConfig.subsectionsForModels.values}
                            activeValue={params.subsection}
                            setValues={updateFilter('subsection')}
                        />
                    </FilterPopover>
                </Badge>
            }
        </div>
    )
}

export default FilterBar
